/**
 * Organization Preferences (Practice Settings)
 *
 * Consolidated, table-styled list of organization-wide defaults — mirrors the
 * PermissionsMatrix card/table treatment so preferences read as one coherent
 * section rather than a series of one-off cards. Add future org-level
 * preferences as additional rows here.
 */
import { useCallback } from 'react';
import { useBlockSigningUntilPlaceholdersFilled } from '@features/user';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  useGetPatientVisibilityQuery,
  useUpdateOrganizationPreferencesMutation,
  useUpdatePatientVisibilityMutation,
} from '../store/api';

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: { message?: unknown } }).data;
    if (typeof data?.message === 'string') return data.message;
  }
  return fallback;
}

export function OrganizationPreferencesTable() {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: visibilityData,
    isLoading: isVisibilityLoading,
    refetch: refetchVisibility,
  } = useGetPatientVisibilityQuery();
  const [updateVisibility, { isLoading: isUpdatingVisibility }] =
    useUpdatePatientVisibilityMutation();

  const blockSigning = useBlockSigningUntilPlaceholdersFilled();
  const [updatePreferences, { isLoading: isUpdatingPreferences }] =
    useUpdateOrganizationPreferencesMutation();

  const currentMode = visibilityData?.mode || 'provider_only';

  const handleVisibilityChange = useCallback(
    async (event: SelectChangeEvent) => {
      try {
        const result = await updateVisibility({
          providerUpdatePatientVisibilityRequest: { mode: event.target.value },
        }).unwrap();
        if (result.success) {
          enqueueSnackbar(result.message || 'Patient visibility updated', {
            variant: 'success',
          });
          refetchVisibility();
        }
      } catch (error: unknown) {
        enqueueSnackbar(
          getErrorMessage(error, 'Failed to update patient visibility'),
          { variant: 'error' },
        );
      }
    },
    [updateVisibility, enqueueSnackbar, refetchVisibility],
  );

  const handleNoteSigningChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.checked;
      try {
        await updatePreferences({
          updateOrganizationPreferencesRequest: {
            block_signing_until_placeholders_filled: nextValue,
          },
        }).unwrap();
        enqueueSnackbar(
          nextValue
            ? 'Providers will now be reminded to fill placeholders before signing.'
            : 'Placeholder reminder before signing turned off.',
          { variant: 'success' },
        );
      } catch {
        enqueueSnackbar('Failed to update setting. Please try again.', {
          variant: 'error',
        });
      }
    },
    [updatePreferences, enqueueSnackbar],
  );

  if (isVisibilityLoading) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Loading preferences...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardHeader
        avatar={<TuneIcon color="primary" />}
        title="Preferences"
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        subheader="Organization-wide defaults applied to all providers in your practice."
      />

      <CardContent sx={{ pt: 0 }}>
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '64%' }} />
              <col style={{ width: '36%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Preference</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Setting
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Patient visibility
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    How patient information is shared across your organization.
                    Applies to all providers immediately.
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Select
                    size="small"
                    value={currentMode}
                    disabled={isUpdatingVisibility}
                    onChange={handleVisibilityChange}
                    sx={{ minWidth: 180 }}
                    aria-label="Patient visibility mode"
                  >
                    <MenuItem value="provider_only">Provider only</MenuItem>
                    <MenuItem value="organization_wide">
                      Organization wide
                    </MenuItem>
                  </Select>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Fill placeholders before signing
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Remind providers to fill in any bracketed placeholders (text
                    in [brackets]) before signing a note. They can still sign if
                    needed.
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Switch
                    checked={blockSigning}
                    disabled={isUpdatingPreferences}
                    onChange={handleNoteSigningChange}
                    slotProps={{
                      input: {
                        'aria-label':
                          'Remind providers to fill placeholders before signing',
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
